import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Calendar, MapPin, ArrowUpRight, Ticket } from 'lucide-react';
import api from '../../lib/api';
import { formatEventDate, isEventPast } from '../../lib/dateUtils';
import EventPromoBadge from '../EventPromoBadge';

/**
 * Bento-style upcoming events. The first card spans more columns (hero card),
 * the rest fill the grid asymmetrically. Mobile collapses to single column.
 */
export default function BentoUpcomingEvents() {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let mounted = true;
    (async () => {
      try {
        const res = await api.get('/events/');
        const list = Array.isArray(res.data) ? res.data : [];
        const future = list
          .filter((e) => e.status === 'published' && !isEventPast(e.date, e.time))
          .sort((a, b) => new Date(a.date) - new Date(b.date));
        if (mounted) setEvents(future);
      } catch (e) {
        if (mounted) setEvents([]);
      } finally {
        if (mounted) setLoading(false);
      }
    })();
    return () => { mounted = false; };
  }, []);

  if (loading) {
    return <SkeletonGrid />;
  }
  if (events.length === 0) {
    return (
      <div
        className="text-center py-16 border border-dashed border-white/10 rounded-2xl"
        data-testid="bento-empty"
      >
        <p className="text-white/40 text-sm">Sin eventos publicados todavía. Volvé pronto.</p>
      </div>
    );
  }

  // Picks: 1 hero (lg:col-span-8 row-span-2) + the rest in standard cards
  const [hero, ...others] = events;

  return (
    <div
      className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5"
      data-testid="bento-grid"
    >
      <BentoCard
        event={hero}
        className="lg:col-span-8 lg:row-span-2 min-h-[420px] lg:min-h-[600px]"
        isHero
      />
      {others.map((e, i) => (
        <BentoCard
          key={e.id}
          event={e}
          className="lg:col-span-4 min-h-[220px] lg:min-h-[290px]"
          delay={(i + 1) * 0.05}
        />
      ))}
    </div>
  );
}

function BentoCard({ event, className = '', isHero = false, delay = 0 }) {
  const accent = event.producer_branding?.primary_color || '#D4AF37';
  const dateLabel = formatEventDate(event.date, "EEE d MMM");
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-80px' }}
      transition={{ duration: 0.6, delay, ease: [0.22, 1, 0.36, 1] }}
      className={className}
    >
      <Link
        to={`/evento/${event.id}`}
        className="group relative h-full w-full overflow-hidden rounded-2xl border border-white/8 hover:border-white/25 transition-all duration-500 block bg-[#0A0A0A]"
        data-testid={`bento-event-${event.id}`}
      >
        {/* Background image */}
        {event.image_url ? (
          <div
            className="absolute inset-0 bg-cover bg-center transition-transform duration-[1500ms] group-hover:scale-[1.06]"
            style={{ backgroundImage: `url(${event.image_url})` }}
          />
        ) : (
          <div
            className="absolute inset-0"
            style={{ background: `linear-gradient(135deg, ${accent}25, transparent 60%), #0A0A0A` }}
          />
        )}
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-black via-black/70 to-transparent" />
        <div className="absolute inset-0 bg-gradient-to-br from-black/40 via-transparent to-transparent" />

        {/* Top tags */}
        <div className="absolute top-4 left-4 right-4 flex items-start justify-between gap-3">
          <div className="flex flex-col items-start gap-2">
            <span
              className="inline-flex items-center gap-1.5 backdrop-blur-md bg-black/40 border border-white/15 rounded-full px-3 py-1.5 text-[10px] font-bold uppercase tracking-[0.18em] text-white"
              style={{ fontFamily: "'Manrope', sans-serif" }}
            >
              <Calendar size={11} style={{ color: accent }} />
              {dateLabel}
            </span>
            <EventPromoBadge promo={event.active_promo} />
          </div>
          <span className="hidden sm:inline-flex items-center justify-center w-9 h-9 rounded-full bg-white/5 border border-white/15 text-white/80 group-hover:bg-[#D4AF37] group-hover:text-black group-hover:border-[#D4AF37] transition-all">
            <ArrowUpRight size={14} />
          </span>
        </div>

        {/* Bottom content */}
        <div className="absolute bottom-0 left-0 right-0 p-5 sm:p-6 lg:p-8">
          {event.producer_name && (
            <p
              className="text-[10px] sm:text-[11px] uppercase tracking-[0.3em] mb-3 font-bold"
              style={{ color: accent, fontFamily: "'Unbounded', sans-serif" }}
            >
              {event.producer_name}
            </p>
          )}
          <h3
            className={`font-black text-white leading-[0.95] tracking-tighter mb-3 uppercase ${isHero ? 'text-3xl sm:text-5xl lg:text-6xl' : 'text-xl sm:text-2xl'}`}
            style={{ fontFamily: "'Unbounded', sans-serif" }}
          >
            {event.title}
          </h3>
          <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-white/60 text-xs">
            {event.location && (
              <span className="inline-flex items-center gap-1.5">
                <MapPin size={11} />
                <span className="truncate max-w-[180px]">{event.location}</span>
              </span>
            )}
            {event.time && (
              <span className="inline-flex items-center gap-1.5">
                <span className="opacity-70">·</span>
                {event.time} hs
              </span>
            )}
            {isHero && event.ticket_price > 0 && (
              <span className="inline-flex items-center gap-1.5">
                <span className="opacity-70">·</span>
                <Ticket size={11} />
                desde ${event.ticket_price.toLocaleString('es-AR')}
              </span>
            )}
          </div>
        </div>
      </Link>
    </motion.div>
  );
}

function SkeletonGrid() {
  return (
    <div className="grid grid-cols-1 lg:grid-cols-12 gap-4 sm:gap-5" data-testid="bento-loading">
      <div className="lg:col-span-8 lg:row-span-2 min-h-[420px] lg:min-h-[600px] rounded-2xl bg-white/[0.03] animate-pulse" />
      {[0, 1, 2, 3].map((i) => (
        <div key={i} className="lg:col-span-4 min-h-[220px] lg:min-h-[290px] rounded-2xl bg-white/[0.03] animate-pulse" />
      ))}
    </div>
  );
}
